import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  downloadAdminManagedDocument,
  listAdminManagedDocuments,
  uploadAdminManagedDocument,
} from '../../api/adminService';
import { useToast } from '../../components/Toast';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const todayIso = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const describeSource = (doc) => {
  if (!doc) return '';
  if (doc.source === 'uploaded') {
    return `${formatDateTime(doc.uploaded_at)} 업로드 · ${doc.original_filename || 'PDF'} · ${doc.uploaded_by}`;
  }
  if (doc.source === 'bundled') return '기본 파일을 쓰고 있어요. 새로 발급받은 PDF를 올리면 바로 바뀌어요.';
  return '파일이 없어요. PDF를 올려주세요.';
};

export const describeFreshness = (doc) => {
  if (!doc?.issued_on) {
    return doc?.stale_after_days ? { tone: 'muted', text: '발급일을 함께 입력하면 오래된 서류를 알려드려요.' } : null;
  }
  const days = Number(doc.days_since_issued || 0);
  if (doc.stale) {
    return { tone: 'warning', text: `${doc.issued_on} 발급 · ${days}일 지났어요. 새로 발급받아 올려주세요.` };
  }
  return { tone: 'ok', text: `${doc.issued_on} 발급 · ${days}일 지났어요.` };
};

const AdminManagedDocumentsPanel = () => {
  const { showToast } = useToast();
  const [documents, setDocuments] = useState(null);
  const [busy, setBusy] = useState({ type: '', action: '' });
  const [issuedOn, setIssuedOn] = useState({});
  const inputRef = useRef(null);
  const pendingTypeRef = useRef('');

  const load = useCallback(async () => {
    try {
      setDocuments(await listAdminManagedDocuments());
    } catch (err) {
      setDocuments([]);
      showToast(err.message || '서류 정보를 불러오지 못했어요.', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (doc) => {
    setBusy({ type: doc.type, action: 'download' });
    try {
      const blob = await downloadAdminManagedDocument(doc.type);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.download_filename || `${doc.label}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err.message || '서류를 내려받지 못했어요.', 'error');
    } finally {
      setBusy({ type: '', action: '' });
    }
  };

  const pickFile = (docType) => {
    pendingTypeRef.current = docType;
    inputRef.current?.click();
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const docType = pendingTypeRef.current;
    if (!file || !docType) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      showToast('PDF 파일만 올릴 수 있어요.', 'error');
      return;
    }
    setBusy({ type: docType, action: 'upload' });
    try {
      const updated = await uploadAdminManagedDocument(docType, file, issuedOn[docType] || '');
      setDocuments((prev) => (prev || []).map((doc) => (doc.type === docType ? updated : doc)));
      setIssuedOn((prev) => ({ ...prev, [docType]: '' }));
      showToast(`${updated.label}를 새 파일로 바꿨어요. 이제 모든 서류에 새 파일이 들어가요.`, 'success', 6000);
    } catch (err) {
      showToast(err.message || '서류를 올리지 못했어요.', 'error');
    } finally {
      setBusy({ type: '', action: '' });
    }
  };

  return (
    <section className="admin-ops__doc-panel admin-ops__doc-panel--list" aria-label="행정 서류 관리">
      <div className="admin-ops__doc-panel-head">
        <strong>행정 서류 관리</strong>
        <small>새 파일을 올리면 견적·단체 결제 메일, 업무 서류, 선생님 증빙 서류에 바로 새 파일이 들어가요.</small>
      </div>
      {documents === null && <span>불러오는 중...</span>}
      {(documents || []).map((doc) => {
        const freshness = describeFreshness(doc);
        const isBusy = Boolean(busy.action);
        return (
          <div key={doc.type} className="admin-ops__doc-row" data-testid={`managed-doc-${doc.type}`}>
            <div className="admin-ops__doc-row-info">
              <strong>{doc.label}</strong>
              <span>{describeSource(doc)}</span>
              {freshness && (
                <span className={`admin-ops__doc-freshness admin-ops__doc-freshness--${freshness.tone}`}>
                  {freshness.text}
                </span>
              )}
            </div>
            <div className="admin-ops__doc-panel-actions">
              <label className="admin-ops__doc-issued">
                <span>새 파일 발급일</span>
                <input
                  type="date"
                  max={todayIso()}
                  value={issuedOn[doc.type] || ''}
                  onChange={(e) => setIssuedOn((prev) => ({ ...prev, [doc.type]: e.target.value }))}
                  aria-label={`${doc.label} 발급일`}
                />
              </label>
              <button type="button" className="admin-ops__secondary" onClick={() => download(doc)} disabled={isBusy}>
                {busy.type === doc.type && busy.action === 'download' ? '내려받는 중...' : '현재 파일 내려받기'}
              </button>
              <button type="button" className="admin-ops__primary" onClick={() => pickFile(doc.type)} disabled={isBusy}>
                {busy.type === doc.type && busy.action === 'upload' ? '올리는 중...' : '새 PDF로 바꾸기'}
              </button>
            </div>
          </div>
        );
      })}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        data-testid="managed-doc-file-input"
        onClick={(e) => e.stopPropagation()}
        onChange={upload}
      />
    </section>
  );
};

export default AdminManagedDocumentsPanel;
